import { useState, useEffect } from 'react'

export default function DeeperAnalysisDropdown({ analysisId, supabase, initialDeeperAnalysis = null }) {
  const [followUps, setFollowUps] = useState([])
  const [loading, setLoading] = useState(true)
  const [expandedIndex, setExpandedIndex] = useState(null)

  useEffect(() => {
    if (analysisId) {
      fetchFollowUps()
    } else if (initialDeeperAnalysis) {
      setFollowUps([{ result_json: initialDeeperAnalysis, created_at: new Date().toISOString(), follow_up_depth: 2, confidence_improvement: 0 }])
      setExpandedIndex(0)
      setLoading(false)
    } else {
      setLoading(false)
    }
  }, [analysisId, initialDeeperAnalysis])

  async function fetchFollowUps() {
    try {
      const { data, error } = await supabase
        .from('analysis_follow_ups')
        .select('*')
        .eq('analysis_id', analysisId)
        .order('created_at', { ascending: false })

      if (error) {
        console.error('Error fetching follow-ups:', error)
      } else if (data) {
        setFollowUps(data)
        if (data.length > 0) {
          setExpandedIndex(0)
        }
      }
    } catch (err) {
      console.error('Unexpected error fetching follow-ups:', err)
    } finally {
      setLoading(false)
    }
  }

  if (loading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <div className="spinner" style={{ width: '20px', height: '20px', borderWidth: '2px', margin: '0 auto' }}></div>
      </div>
    )
  }

  if (followUps.length === 0) {
    return null
  }

  return (
    <div style={{ marginTop: '28px' }}>
      <h4 style={{ fontSize: '20px', marginBottom: '16px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 15px rgba(152, 251, 152, 0.7)' }}>
        🔬 Deeper Analysis Results ({followUps.length})
      </h4>

      {followUps.map((followUp, index) => {
        const deeperAnalysis = followUp.result_json
        const isExpanded = expandedIndex === index
        const timeSince = new Date(followUp.created_at).toLocaleString()

        return (
          <div
            key={followUp.id || index}
            style={{
              marginBottom: '20px',
              padding: '24px',
              background: 'linear-gradient(135deg, rgba(152, 251, 152, 0.2) 0%, rgba(34, 139, 34, 0.35) 100%)',
              backdropFilter: 'blur(16px)',
              WebkitBackdropFilter: 'blur(16px)',
              borderRadius: '16px',
              border: '2px solid rgba(152, 251, 152, 0.5)',
              boxShadow: '0 8px 20px rgba(34, 139, 34, 0.3), 0 0 30px rgba(152, 251, 152, 0.3)'
            }}
          >
            <div
              onClick={() => setExpandedIndex(isExpanded ? null : index)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                cursor: 'pointer',
                marginBottom: isExpanded ? '20px' : '0'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                <span style={{ fontSize: '24px' }}>🔬</span>
                <div>
                  <h5 style={{ fontSize: '18px', fontWeight: '700', color: '#98FB98', margin: 0, textShadow: '0 0 12px rgba(152, 251, 152, 0.8)' }}>
                    Deeper Analysis - Level {followUp.follow_up_depth || 2}
                  </h5>
                  <div style={{ fontSize: '13px', color: 'rgba(152, 251, 152, 0.8)', marginTop: '4px' }}>
                    Completed: {timeSince}
                  </div>
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                {followUp.confidence_improvement !== null && followUp.confidence_improvement !== undefined && (
                  <div style={{
                    padding: '6px 12px',
                    background: followUp.confidence_improvement > 0 ? 'rgba(152, 251, 152, 0.3)' : 'rgba(255, 200, 100, 0.3)',
                    border: `1px solid ${followUp.confidence_improvement > 0 ? 'rgba(152, 251, 152, 0.6)' : 'rgba(255, 200, 100, 0.6)'}`,
                    borderRadius: '6px',
                    fontSize: '13px',
                    fontWeight: '700',
                    color: followUp.confidence_improvement > 0 ? '#98FB98' : '#FFA500',
                    whiteSpace: 'nowrap'
                  }}>
                    {followUp.confidence_improvement > 0 ? '+' : ''}{followUp.confidence_improvement.toFixed(1)}% confidence
                  </div>
                )}

                <span style={{
                  fontSize: '24px',
                  transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: '#98FB98'
                }}>
                  ▼
                </span>
              </div>
            </div>

            {isExpanded && deeperAnalysis && (
              <div style={{ animation: 'fadeIn 0.3s ease' }}>
                {deeperAnalysis.verification_summary && (
                  <div style={{ marginBottom: '20px', padding: '16px', background: 'rgba(34, 139, 34, 0.2)', borderRadius: '10px', border: '1px solid rgba(152, 251, 152, 0.3)' }}>
                    <h6 style={{ fontSize: '14px', fontWeight: '700', color: '#98FB98', marginBottom: '10px', textShadow: '0 0 8px rgba(152, 251, 152, 0.6)' }}>
                      Verification Summary
                    </h6>
                    <p style={{ fontSize: '14px', color: 'rgba(152, 251, 152, 0.95)', lineHeight: '1.7', margin: 0, textShadow: '0 0 5px rgba(152, 251, 152, 0.4)' }}>
                      {deeperAnalysis.verification_summary}
                    </p>
                  </div>
                )}

                {deeperAnalysis.confidence_breakdown && (
                  <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ fontSize: '14px', fontWeight: '700', color: '#98FB98', marginBottom: '12px', textShadow: '0 0 8px rgba(152, 251, 152, 0.6)' }}>
                      Confidence Breakdown
                    </h6>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                      {Object.entries(deeperAnalysis.confidence_breakdown).map(([key, value]) => (
                        <div key={key} style={{ padding: '12px', background: 'rgba(34, 139, 34, 0.15)', borderRadius: '8px', border: '1px solid rgba(152, 251, 152, 0.25)' }}>
                          <div style={{ fontSize: '12px', color: 'rgba(152, 251, 152, 0.8)', marginBottom: '6px', textTransform: 'capitalize' }}>
                            {key.replace(/_/g, ' ')}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <div style={{ flex: 1, height: '8px', background: 'rgba(34, 139, 34, 0.3)', borderRadius: '4px', overflow: 'hidden' }}>
                              <div style={{
                                width: `${value}%`,
                                height: '100%',
                                background: 'linear-gradient(90deg, #98FB98 0%, #228B22 100%)',
                                borderRadius: '4px',
                                boxShadow: '0 0 8px rgba(152, 251, 152, 0.6)'
                              }} />
                            </div>
                            <div style={{ fontSize: '16px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 10px rgba(152, 251, 152, 0.7)', minWidth: '45px', textAlign: 'right' }}>
                              {value}%
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {deeperAnalysis.alternative_analysis && deeperAnalysis.alternative_analysis.length > 0 && (
                  <div style={{ marginBottom: '20px' }}>
                    <h6 style={{ fontSize: '14px', fontWeight: '700', color: '#98FB98', marginBottom: '12px', textShadow: '0 0 8px rgba(152, 251, 152, 0.6)' }}>
                      Alternative Companies Considered
                    </h6>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                      {deeperAnalysis.alternative_analysis.map((alt, idx) => (
                        <div key={idx} style={{ padding: '12px', background: 'rgba(34, 139, 34, 0.15)', borderRadius: '8px', border: '1px solid rgba(152, 251, 152, 0.25)' }}>
                          <div style={{ fontSize: '14px', fontWeight: '600', color: '#98FB98', marginBottom: '6px' }}>
                            {alt.company}
                          </div>
                          <div style={{ fontSize: '13px', color: 'rgba(152, 251, 152, 0.85)', lineHeight: '1.6' }}>
                            {alt.reason_ruled_out}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {(deeperAnalysis.certainty_metric !== null && deeperAnalysis.certainty_metric !== undefined) && (
                  <div style={{ padding: '16px', background: 'rgba(34, 139, 34, 0.2)', borderRadius: '10px', border: '1px solid rgba(152, 251, 152, 0.3)' }}>
                    <h6 style={{ fontSize: '14px', fontWeight: '700', color: '#98FB98', marginBottom: '10px', textShadow: '0 0 8px rgba(152, 251, 152, 0.6)' }}>
                      Overall Certainty
                    </h6>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ flex: 1, height: '12px', background: 'rgba(34, 139, 34, 0.3)', borderRadius: '6px', overflow: 'hidden' }}>
                        <div style={{
                          width: `${deeperAnalysis.certainty_metric ?? 0}%`,
                          height: '100%',
                          background: 'linear-gradient(90deg, #98FB98 0%, #228B22 100%)',
                          borderRadius: '6px',
                          boxShadow: '0 0 10px rgba(152, 251, 152, 0.6)'
                        }} />
                      </div>
                      <div style={{ fontSize: '18px', fontWeight: '700', color: '#98FB98', textShadow: '0 0 10px rgba(152, 251, 152, 0.7)' }}>
                        {deeperAnalysis.certainty_metric ?? 0}%
                      </div>
                    </div>
                  </div>
                )}

                {followUp.uncertainty_reasons && followUp.uncertainty_reasons.length > 0 && (
                  <details style={{ marginTop: '16px' }}>
                    <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: '600', color: 'rgba(152, 251, 152, 0.9)', marginBottom: '8px' }}>
                      View Uncertainty Context ({followUp.uncertainty_reasons.length} issues addressed)
                    </summary>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginTop: '8px', paddingLeft: '16px' }}>
                      {followUp.uncertainty_reasons.map((reason, idx) => (
                        <span key={idx} style={{
                          padding: '4px 10px',
                          background: 'rgba(255, 165, 0, 0.2)',
                          border: '1px solid rgba(255, 165, 0, 0.4)',
                          borderRadius: '4px',
                          fontSize: '11px',
                          fontWeight: '600',
                          color: 'rgba(255, 200, 100, 0.95)',
                          fontFamily: 'monospace'
                        }}>
                          {typeof reason === 'string' ? reason.replace(/_/g, ' ') : JSON.stringify(reason)}
                        </span>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}
